/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthStore } from '../stores/auth.store';

export interface PrevQuote {
  isin: string;
  bid: number;
  ask: number;
}

@Injectable({
  providedIn: 'root',
})
export class PrevQuoteService {
  private readonly httpClient = inject(HttpClient);
  private readonly authStore = inject(AuthStore);

  /** Cache per ISIN list */
  private cache = new Map<string, Observable<Record<string, any>>>();

  public getPrevQuotes(isins: string[]): Observable<Record<string, any>> {
    if (!isins.length) {
      return of({});
    }

    const token = this.authStore.token();
    if (!token) {
      return of({});
    }

    const key = isins.join(',');

    // Return cached observable if it exists
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }

    const headers = {
      'X-GB-Authorization': token,
    };

    const url = `${environment.apihost}/api/quote/prev?isins=${key}`;

    const request$ = this.httpClient
      .get<Record<string, any>>(url, { withCredentials: true, headers })
      .pipe(
        catchError((err) => {
          if (err.status === 401 && environment.apihost_fallback) {
            const fallbackUrl = `${environment.apihost_fallback}/api/quote/prev?isins=${key}`;
            return this.httpClient.get<Record<string, any>>(fallbackUrl, {
              withCredentials: true,
              headers,
            });
          }
          return throwError(() => err);
        }),
        shareReplay(1),
      );

    this.cache.set(key, request$);

    return request$;
  }
}
